import { useState, useRef, useCallback, useEffect } from 'react';
import { PlayCircleFilled } from '@mui/icons-material';
import { Box, Button, styled } from '@mui/material';
import ReactPlayer from 'react-player';
import { loadYouTubeIframeApi, isYouTubeUrl } from './load-youtube-api';

interface VideoPlayerProps {
  url: string;
  onProgress?: (percentage: number) => void;
  onPause?: () => void;
  onEnded?: () => void;
  fullWidth?: boolean;
}

const PreviewButton = () => (
  <Button sx={{ zIndex: 3 }} variant="contained" endIcon={<PlayCircleFilled />}>
    Assistir
  </Button>
);

const Overlay = styled(Box)({
  position: 'absolute',
  inset: 0,
  backgroundColor: 'rgba(0,0,0,0.45)',
  pointerEvents: 'none',
});

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullWidth',
})<{ fullWidth?: boolean }>(({ fullWidth }) => ({
  position: 'relative',
  width: '100%',
  maxWidth: fullWidth ? '100%' : 800,
  minWidth: fullWidth ? 0 : 450,
  height: 450,
  overflow: 'hidden',
}));

export const VideoPlayer = ({
  url,
  onProgress,
  onPause,
  onEnded,
  fullWidth = false,
}: VideoPlayerProps) => {
  const playerRef = useRef<HTMLVideoElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [seeking, setSeeking] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [apiReady, setApiReady] = useState(() => !isYouTubeUrl(url));

  const showOverlay = !playing && !hasStarted;

  // Load the IFrame API through our proxy before mounting a YouTube player.
  useEffect(() => {
    if (!isYouTubeUrl(url)) {
      setApiReady(true);
      return;
    }

    let active = true;
    setApiReady(false);
    loadYouTubeIframeApi().then(() => {
      if (active) setApiReady(true);
    });

    return () => {
      active = false;
    };
  }, [url]);

  const setPlayerRef = useCallback((player: HTMLVideoElement) => {
    if (!player) return;
    playerRef.current = player;
  }, []);

  const handleTimeUpdate = () => {
    const player = playerRef.current;
    if (!player || seeking || !player.duration) return;

    const played = player.currentTime / player.duration;
    onProgress?.(Math.round(played * 100));
  };

  const handleSeeking = () => {
    setSeeking(true);
  };

  const handleSeeked = () => {
    setSeeking(false);
  };

  const handlePause = () => {
    setPlaying(false);
    onPause?.();
  };

  const handlePlay = () => {
    setPlaying(true);
    setHasStarted(true);
  };

  const handleEnded = () => {
    setPlaying(false);
    onEnded?.();
  };

  const handleRateChange = () => {
    const player = playerRef.current;
    if (player?.playbackRate) {
      setPlaybackRate(player.playbackRate);
    }
  };

  if (!url) return null;

  return (
    <Container
      fullWidth={fullWidth}
      borderRadius={({ shape }) => `${shape.borderRadius}px`}
    >
      {apiReady && (
        <ReactPlayer
          key={url}
          ref={setPlayerRef}
          className="react-player"
          src={url}
          playing={playing}
          playbackRate={playbackRate}
          controls
          light
          playIcon={<PreviewButton />}
          onClickPreview={() => setPlaying(true)}
          onPlay={handlePlay}
          onPause={handlePause}
          onRateChange={handleRateChange}
          onTimeUpdate={handleTimeUpdate}
          onSeeking={handleSeeking}
          onSeeked={handleSeeked}
          onEnded={handleEnded}
          width="100%"
          height="100%"
          style={{ position: 'absolute', inset: 0 }}
          config={{
            youtube: {
              cc_load_policy: 1,
              cc_lang_pref: 'pt',
              rel: 0,
              origin: window.location.origin,
            },
          }}
        />
      )}
      {showOverlay && <Overlay />}
    </Container>
  );
};
